import { SagaIterator } from 'redux-saga';
import { put, call, takeLatest, StrictEffect } from 'redux-saga/effects';
import {
  fetchIssues,
  fetchIssuesDashboard,
  fetchIssuesDashboardError,
  fetchIssuesDashboardSuccess,
  fetchIssuesError,
  fetchIssuesSearch,
  fetchIssuesSearchError,
  fetchIssuesSearchSuccess,
  fetchIssuesSuccess,
  putIssueStatus,
  fetchUserReportedIssuesSearch,
  fetchUserReportedIssuesSearchSuccess,
  fetchUserReportedIssuesSearchError
} from './index';
import { apiInstance } from 'src/services/interceptor';
import { PayloadAction } from '@reduxjs/toolkit';
import { fetchMyReports } from '../myReports';
import { fetchIssue } from '../issue';

interface PayloadProps {
  siteId: string;
  reportNumber?: string | null;
  searchType?: string | null;
  search?: string | null;
  status?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  nextToken?: string | null;
}

function* getIssues({ payload: { siteId } }: PayloadAction<PayloadProps>): SagaIterator {
  try {
    if (siteId) {
      const response = yield call(apiInstance, `/sites/${siteId}/issues`);
      yield put(fetchIssuesSuccess(response.data));
    }
  } catch (e) {
    yield put(fetchIssuesError());
  }
}

function* getIssuesSearch({
  payload: { siteId, reportNumber = '', status = '', dateFrom = '', dateTo = '', nextToken = '' },
}: PayloadAction<PayloadProps>): SagaIterator {
  try {
    if (siteId) {
      const response = yield call(
        apiInstance,
        `/sites/${siteId}/issues?search=${reportNumber}&status=${
          status || ''
        }&dateFrom=${dateFrom}&dateTo=${dateTo}&nextToken=${nextToken}`,
      );
      yield put(fetchIssuesSearchSuccess(response.data));
    }
  } catch (e) {
    yield put(fetchIssuesSearchError());
  }
}

function* getUserReportedIssuesSearch({
  payload: { siteId, status = '', dateFrom = '', dateTo = '', nextToken = '', searchType = '', search = '' },
}: PayloadAction<PayloadProps>): SagaIterator {
  try {
    let newSearchType = searchType
    let newSearchText = search
    if (!search) {
      newSearchText = ''
      newSearchType = ''
    }
    const response = yield call(
      apiInstance,
      `/sites/${siteId}/issues?search=${newSearchText}&searchType=${newSearchType}&status=${
        status || ''
      }&dateFrom=${dateFrom}&dateTo=${dateTo}&nextToken=${nextToken}`,
    );
    yield put(fetchUserReportedIssuesSearchSuccess(response.data));
  } catch (e) {
    yield put(fetchUserReportedIssuesSearchError());
  }
}

interface PutIssuesPayloadProps {
  siteId: string;
  issueId: string;
  data: {
    status: string;
  };
  userRole?: string;
}

function* putIssuesStatusSaga({ payload }: PayloadAction<PutIssuesPayloadProps>): SagaIterator {
  try {
    yield call(apiInstance.put, `sites/${payload.siteId}/issues/${payload.issueId}`, payload.data);
    yield put(fetchMyReports());
    yield put(fetchIssues({ siteId: payload.siteId }));
    if (payload.userRole === 'webowner') {
      yield put(fetchIssuesDashboard({ siteId: payload.siteId }));
    } else if (payload.userRole === 'view' || payload.userRole === 'userView') {
      yield put(fetchIssue({ siteId: payload.siteId, issueId: payload.issueId }));
    }
  } catch (e) {
    yield put(fetchIssuesError());
  }
}

function* getIssuesDashboar({ payload: { siteId } }: PayloadAction<PayloadProps>): SagaIterator {
  try {
    if (siteId) {
      const response = yield call(apiInstance, `/issues/dashboard/${siteId}`);
      yield put(fetchIssuesDashboardSuccess(response.data));
    }
  } catch (e) {
    yield put(fetchIssuesDashboardError());
  }
}

function* issuesSaga(): Generator<StrictEffect, void> {
  yield takeLatest(fetchIssues.type, getIssues);
  yield takeLatest(fetchIssuesSearch.type, getIssuesSearch);
  yield takeLatest(fetchUserReportedIssuesSearch.type, getUserReportedIssuesSearch)
  yield takeLatest(putIssueStatus.type, putIssuesStatusSaga);
  yield takeLatest(fetchIssuesDashboard.type, getIssuesDashboar);
}

export { issuesSaga };
